/**
 * `demo/guide/GuideChooser.tsx` — the card behind Help → "Show the guide"
 * (#2824). It sits in the middle of a dimmed page.
 */
import { SpotlightFrame } from "../../tour/SpotlightFrame";
import type { AutoplayTarget } from "./events";

const NO_ELEMENT = () => null;

const rowClass =
  "flex w-full flex-col rounded-md border border-border px-3 py-2 text-left text-sm transition-colors hover:border-accent/50 hover:bg-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

export interface GuideChooserProps {
  /** Only when the visitor stopped somewhere worth returning to. */
  canContinue: boolean;
  /** The words on the continue row. Default "Continue where I stopped". */
  continueLabel?: string;
  onContinue: () => void;
  onRestart: () => void;
  onClose: () => void;
  /** Autoplay: play the introduction or this page's tour by itself. A scenario is picked with "Change the scenario". */
  onAutoplay?: (target: AutoplayTarget) => void;
  /** True when this page has a tour. */
  hasPageTour?: boolean;
}

export function GuideChooser({
  canContinue,
  continueLabel = "Continue where I stopped",
  onContinue,
  onRestart,
  onClose,
  onAutoplay,
  hasPageTour = false,
}: GuideChooserProps) {
  return (
    <SpotlightFrame
      testIdPrefix="demo-guide"
      stepKey="chooser"
      getElement={NO_ELEMENT}
      centered
      passThrough
      surface="guide"
      onEscape={onClose}
    >
      {({ titleId, bodyId }) => (
        <>
          <h2 id={titleId} className="text-base font-semibold tracking-tight">
            Show the guide
          </h2>
          <p id={bodyId} className="sr-only">
            Continue where you stopped, or start the guide again.
          </p>
          <div className="mt-3 flex flex-col gap-2" data-testid="demo-guide-chooser">
            {canContinue ? (
              <button
                type="button"
                className={rowClass}
                onClick={onContinue}
                data-testid="demo-guide-continue"
              >
                <span className="font-medium text-fg">{continueLabel}</span>
              </button>
            ) : null}
            <button
              type="button"
              className={rowClass}
              onClick={onRestart}
              data-testid="demo-guide-restart"
            >
              <span className="font-medium text-fg">Start from the beginning</span>
            </button>
          </div>
          {onAutoplay ? (
            <div className="mt-3 flex flex-col gap-2" data-testid="demo-guide-autoplay-list">
              <p className="text-xs font-medium text-fg-muted">Autoplay: watch it play by itself</p>
              <button
                type="button"
                className={rowClass}
                onClick={() => onAutoplay({ kind: "intro" })}
                data-testid="demo-guide-autoplay-intro"
              >
                <span className="font-medium text-fg">Play the introduction</span>
              </button>
              {hasPageTour ? (
                <button
                  type="button"
                  className={rowClass}
                  onClick={() => onAutoplay({ kind: "page" })}
                  data-testid="demo-guide-autoplay-page"
                >
                  <span className="font-medium text-fg">Play this page's tour</span>
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm text-xs text-fg-muted underline-offset-4 hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              data-testid="demo-guide-skip"
            >
              Close
            </button>
          </div>
        </>
      )}
    </SpotlightFrame>
  );
}
